import React, { FC, useEffect, useMemo, useState } from 'react';
import TwGenericMenu, { TwGenericMenuOptions } from '../../../TwGenericMenu/TwGenericMenu';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { ReactComponent as GenerateIcon } from '../../../../assets/icons/generate-ratecon.svg';
import { ReactComponent as PdfIcon } from '../../../../assets/icons/pdf-file.svg';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { TwButton, TwButtonType } from '../../../TwButtons/TwButtons';
import { LoadInvoiceResponse } from '../../../../redux/api/load/load.model';
import { INVOICE_STATUS } from '../../../../providers/Invoices/Invoices.model';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../../utils/logRocketUtils';
import useAuth from '../../../../providers/Auth/Auth.provider';
import {
  useMarkAsPaidMutation,
  useMarkAsUnpaidMutation
} from '../../../../redux/api/invoice/invoiceApi';
import FactoringService from '../../../../providers/Factoring/Factoring.service';
import useFeatureFlags from '../../../../providers/FeatureFlags/FeatureFlags.provider';
import FactoringUtils from '../../../../utils/factoringUtils';

const TwGenerateInvoice: FC<{
  invoice: LoadInvoiceResponse | null;
  loadId: string;
  handleClickView: () => void;
  handleGenerateClick: () => void;
  handleDeleteInvoice: () => void;
  handleEditInvoiceOpen: () => void;
  handleSendToFactoring: (invoiceId: string | null) => void;
  reloadData?: () => void;
}> = ({
  invoice,
  loadId,
  handleClickView,
  handleGenerateClick,
  handleDeleteInvoice,
  handleEditInvoiceOpen,
  handleSendToFactoring,
  reloadData
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const { enableFactoring } = useFeatureFlags();

  const {
    data: { userData }
  } = useAuth();
  const [markAsPaid] = useMarkAsPaidMutation();
  const [markAsUnpaid] = useMarkAsUnpaidMutation();
  const [invoiceStatus, setInvoiceStatus] = useState('Generated');
  const [hasFactoringConfig, setHasFactoringConfig] = useState(false);
  const [isFactored, setIsFactored] = useState(false);

  useEffect(() => {
    if (!enableFactoring) return;

    const controller = new AbortController();
    setInvoiceStatus('Generated');

    if (!invoice?.division_id) return;

    setIsFactored(false);
    FactoringService.getOrderStatus(invoice?.division_id, [loadId], {
      signal: controller.signal
    }).then((response) => {
      if (!response) return;

      const order = response[0];
      const status = order ? FactoringUtils.getReadableStatus(order.factoring_status) : null;

      if (status) {
        setInvoiceStatus(status);
        setIsFactored(true);
      }
    });

    validateFactoringConfig(controller);

    return (): void => {
      controller.abort();
    };
  }, [invoice, enableFactoring]);

  const validateFactoringConfig = async (controller: AbortController): Promise<void> => {
    setHasFactoringConfig(false);
    if (!invoice || !invoice.division_id) return;

    const divisionId = invoice.division_id;
    FactoringService.getConfig(divisionId, { signal: controller.signal })
      .then((response) => {
        setHasFactoringConfig(!!response);
      })
      .catch((error) => {
        if (error && error.status === 404) {
          FactoringService.getInternalFactoringCompany(divisionId, { signal: controller.signal })
            .then((response) => {
              if (response && response.data) {
                setHasFactoringConfig(true);
              } else {
                setHasFactoringConfig(false);
              }
            })
            .catch(() => {
              setHasFactoringConfig(false);
            });

          return;
        }

        setHasFactoringConfig(false);
      });
  };

  /** Updates invoice status to PAID */
  const handleMarkAsPaid = async (): Promise<void> => {
    if (invoice && invoice.id) {
      markAsPaid({ id: invoice.id, orderId: loadId })
        .unwrap()
        .then((res) => {
          reloadData && reloadData();
          addNotification({
            message: 'Invoice updated successfully',
            variant: 'success'
          });
        })
        .catch((error) => {
          logRocketTrack('Invoice', {
            action: 'Mark invoice as paid',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error updating invoice',
            variant: 'error'
          });
        });
    }
  };

  const handleMarkAsUnpaid = async (): Promise<void> => {
    if (invoice && invoice.id) {
      markAsUnpaid({ id: invoice.id, orderId: loadId })
        .unwrap()
        .then((res) => {
          reloadData && reloadData();
          addNotification({
            message: 'Invoice updated successfully',
            variant: 'success'
          });
        })
        .catch((error) => {
          logRocketTrack('Invoice', {
            action: 'Mark invoice as paid',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error updating invoice',
            variant: 'error'
          });
        });
    }
  };

  const getInvoiceMenu = useMemo((): TwGenericMenuOptions[] => {
    const options: TwGenericMenuOptions[] = [
      {
        label: 'View invoice',
        action: handleClickView
      },

      {
        label: invoice?.status === INVOICE_STATUS.paid ? 'Mark as unpaid' : 'Mark as paid',
        action: (): void => {
          invoice?.status === INVOICE_STATUS.paid ? handleMarkAsUnpaid() : handleMarkAsPaid();
        },
        tooltipText: invoice?.status === INVOICE_STATUS.paid ? 'Already marked as paid' : ''
      },

      {
        label: 'Edit or send',
        action: handleEditInvoiceOpen
      }
    ];

    // If factoring is enabled, the invoice is not factored, and division has factoring config show the option to send to factoring
    if (enableFactoring && hasFactoringConfig && !isFactored) {
      options.push({
        label: 'Send to factoring',
        action: (): void => handleSendToFactoring(invoice ? (invoice.id ? invoice.id : null) : null)
      });
    }

    options.push({
      label: 'Delete invoice',
      className: 'detail-file__delete',
      action: handleDeleteInvoice
    });

    return options;
  }, [invoice, hasFactoringConfig, isFactored, enableFactoring]);

  return (
    <>
      <Box className="generate-rate-con__docs-container">
        <Stack
          flexDirection={'row'}
          onClick={handleClickView}
          className="generate-rate-con__info-section"
        >
          {invoice ? <PdfIcon /> : <GenerateIcon />}
          <Box display="flex" flexDirection={'column'} sx={{ marginLeft: '15px' }}>
            <span className="detail-file__filename">Invoice</span>
            <span className="detail-file__text detail-file__info-gray">
              {invoice ? (
                <>
                  <span
                    className={`detail-file__text ${
                      invoiceStatus === 'Factoring error' ? 'detail-file__factoring-error' : ''
                    }`}
                  >
                    {invoiceStatus}
                  </span>
                  {` • ${formatDistanceToNow(
                    invoice.created_at ? new Date(invoice.created_at) : new Date()
                  )} ago`}
                </>
              ) : (
                'Create and or send an invoice for this order'
              )}
            </span>
          </Box>
        </Stack>

        {invoice ? (
          <TwGenericMenu className="generate-rate-con__menu" options={getInvoiceMenu} />
        ) : (
          <TwButton
            type={TwButtonType.secondary}
            className="generate-rate-con__btn"
            label="Create"
            action={handleGenerateClick}
          />
        )}
      </Box>
    </>
  );
};

export default TwGenerateInvoice;
